import { useMemo, useState } from "react";

import { CaseDiagnosis, User } from "pwapi";

import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SpecimenDetail } from "shared/reducers/specimenDetailReducer";

import { getHighestVersionDiagnosis } from "../utils";

const SpecimenCopyInput = ({
  caseDetailObject,
  user,
  onChange,
  currentSpecimen,
}: {
  caseDetailObject: CaseDetailState;
  user?: User;
  onChange: Function;
  currentSpecimen?: SpecimenDetail;
}) => {
  const [selectedSpecimen, setSelectedSpecimen] = useState<string>("");

  const checkDiagnosis = (diag: CaseDiagnosis) =>
    !!(
      diag.diagnosis ||
      diag.microscopic ||
      diag.macro_code ||
      diag.comment ||
      diag.icd10_code ||
      diag.zsd_code ||
      (diag.cpt_codes && diag.cpt_codes.length > 0)
    );

  const allSpecimenDiagnoses = useMemo(() => {
    return caseDetailObject.specimenDetail.reduce((acc, specimenDetail) => {
      // Ignore current specimen
      if (specimenDetail.id === currentSpecimen?.id) return acc;

      // Get the rough draft if it exists
      const specimenRoughDraft =
        specimenDetail?.diagnoses.filter(
          (d) =>
            d.user &&
            d.user.id === user?.id &&
            !d.is_completed &&
            checkDiagnosis(d)
        ) || [];

      // Get the highest version screen diagnosis if it exists
      const screenDiagnosis = getHighestVersionDiagnosis(
        specimenDetail?.diagnoses.filter(
          (d) => d.source === CaseDiagnosis.source.SCREEN && d.is_completed
        )
      );

      // If either exists, add the specimen and diagnosis option to the dropdown selection object
      if (
        (specimenRoughDraft.length > 0 && specimenRoughDraft[0]) ||
        screenDiagnosis
      ) {
        acc[specimenDetail.name] = specimenRoughDraft[0] || screenDiagnosis;
      }
      return acc;
    }, {} as { [key: string]: CaseDiagnosis | undefined });
    // Only recalculate if the current specimen changes.
  }, [currentSpecimen?.id]);

  const onHandleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const specimenName = e.target.value;
    setSelectedSpecimen(specimenName);

    const diagnosis = {
      diagnosis: allSpecimenDiagnoses[specimenName]?.diagnosis || "",
      microscopic: allSpecimenDiagnoses[specimenName]?.microscopic || "",
      macro_code: allSpecimenDiagnoses[specimenName]?.macro_code || null,
      comment: allSpecimenDiagnoses[specimenName]?.comment || "",
      icd10_code: allSpecimenDiagnoses[specimenName]?.icd10_code || "",
      zsd_code: allSpecimenDiagnoses[specimenName]?.zsd_code || "",
      cpt_codes: allSpecimenDiagnoses[specimenName]?.cpt_codes || [],
      critical_result_override:
        allSpecimenDiagnoses[specimenName]?.critical_result_override ||
        CaseDiagnosis.critical_result_override.DEFAULT,
    };
    onChange(diagnosis);
  };

  return (
    <label>
      Repeat diagnosis from specimen:{" "}
      <select
        className="SpecimenCopyInput__select"
        onChange={onHandleChange}
        value={selectedSpecimen}
      >
        <option value="">...</option>
        {Object.keys(allSpecimenDiagnoses).map((specimenName) => (
          <option key={specimenName} value={specimenName}>
            {specimenName}
          </option>
        ))}
      </select>
    </label>
  );
};

export default SpecimenCopyInput;
